
import { useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { EmergencyRestTimeControls } from "./controls/general/EmergencyRestTimeControls";
import { useEmergencyFieldState } from "./hooks/useEmergencyFieldState";
import { usePersonalPlanDetector } from "./hooks/usePersonalPlanDetector";
import { SetsControl } from "./controls/general/SetsControl";

interface EmergencyExerciseItemProps {
  exercise: {
    id: string;
    name: string;
    sets: number;
    exercise_rest_time?: number | null;
    set_rest_time?: number | null;
    position: number;
  };
  onUpdate: (exerciseId: string, field: string, value: any) => void;
  routineId?: string;
}

export const EmergencyExerciseItem = ({
  exercise,
  onUpdate,
  routineId = ""
}: EmergencyExerciseItemProps) => {
  const [isOpen, setIsOpen] = useState(true);
  const { isPersonalPlan } = usePersonalPlanDetector(routineId);

  // Emergency field states with immediate local updates
  const exerciseRestTimeField = useEmergencyFieldState(
    exercise.exercise_rest_time || null,
    exercise.id,
    "exercise_rest_time",
    onUpdate
  );

  const setRestTimeField = useEmergencyFieldState(
    exercise.set_rest_time || null,
    exercise.id,
    "set_rest_time", 
    onUpdate
  );

  const setsField = useEmergencyFieldState(
    exercise.sets,
    exercise.id,
    "sets",
    onUpdate
  );

  const handleRestTimeChange = useCallback((field: "exercise_rest_time" | "set_rest_time", value: number) => {
    if (field === "exercise_rest_time") {
      exerciseRestTimeField.updateValue(value);
    } else {
      setRestTimeField.updateValue(value);
    }
  }, [exerciseRestTimeField, setRestTimeField]);

  const handleSetsChange = useCallback((value: number) => {
    setsField.updateValue(value);
  }, [setsField]);

  console.log(`🚨 EMERGENCY: Exercise ${exercise.id} - Personal Plan: ${isPersonalPlan}`);

  return (
    <div className="bg-white/10 backdrop-blur-lg rounded-xl p-3 sm:p-4 transition-all">
      <div className="flex justify-between items-center gap-2 mb-4">
        <div className="flex-1">
          <h3 className="font-semibold text-black dark:text-white forest:text-white sunset:text-[#ef5609]">
            {exercise.name}
          </h3>
        </div>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="text-black dark:text-white forest:text-white sunset:text-[#ef5609] hover:opacity-70"
        >
          {isOpen ? "▼" : "▶"}
        </button>
      </div>

      {isOpen && (
        <div className="space-y-4">
          <Card className="p-4 bg-[#2196A3]/20 dark:bg-white/5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SetsControl
                sets={setsField.value}
                onSetsChange={handleSetsChange}
                isPersonalPlan={isPersonalPlan}
              />
              <EmergencyRestTimeControls
                exerciseRestTime={exerciseRestTimeField.value}
                setRestTime={setRestTimeField.value}
                onRestTimeChange={handleRestTimeChange}
                isPersonalPlan={isPersonalPlan}
              />
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};
